import { Box, Center, Flex } from '@televet/kibble-ui/build/chakra';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import format from 'date-fns/format';
import { StatusDescription } from 'pages/Reporting/records/status.records';
import React, { useMemo } from 'react';
import { CareSubscriptionPaymentStatus, Role } from 'shared/types/graphql';
import { centsToDollars, formatCurrency } from '../../utils/currency';
import { getCardBrandIcon } from '../../utils/paymentMethod';
import { CareEnrollmentPaymentData, CareEnrollmentPaymentDataType } from './types';
import { RouteBasePaths } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';

interface CareEnrollmentPaymentBodyProps {
  data: CareEnrollmentPaymentData;
}

export const CareEnrollmentPaymentBody = ({ data }: CareEnrollmentPaymentBodyProps): JSX.Element => {
  const { status, invoiceDate, nextPaymentAttempt, amount, paidAt, paymentMethod, subType } = data;
  const isDispute = subType === CareEnrollmentPaymentDataType.Dispute;
  const { clinicUser } = useClinicUser();
  const isAdmin = useMemo(() => clinicUser?.vetInfo?.roles?.some((r) => r.role === Role.Admin), [clinicUser]);
  const isInRetryPeriod = useMemo(() => nextPaymentAttempt && nextPaymentAttempt > new Date(), [nextPaymentAttempt]);

  return (
    <Box>
      {!isDispute && amount && (
        <Flex alignItems="center" mb={1}>
          <Icon name="invoice" size="sm" variant="subtle" />
          <Text as="p" size="sm" ml={1} variant="subtle">
            Amount: {formatCurrency(centsToDollars(amount), true)}
          </Text>
        </Flex>
      )}
      {invoiceDate && (
        <Flex alignItems="center" mb={1}>
          <Icon name="calendarInfo" size="sm" variant="subtle" />
          <Text as="p" size="sm" ml={1} variant="subtle">
            Invoice Date: {format(invoiceDate, 'MM/dd/yyyy')}
          </Text>
        </Flex>
      )}
      {!isDispute && invoiceDate && !!paymentMethod && (
        <Flex alignItems="center" mb={1}>
          {getCardBrandIcon(paymentMethod.cardBrand || '', 'sm')}
          <Text as="p" size="sm" ml={1} variant="subtle">
            **** **** **** {paymentMethod.last4}
          </Text>
          <Text as="p" size="sm" ml={2} variant="subtle">
            {paymentMethod.expirationMonth}/{paymentMethod.expirationYear?.toString().slice(-2)}
          </Text>
        </Flex>
      )}
      {isDispute && (
        <Flex alignItems="center" mb={1}>
          <Icon name="infoCircle" size="sm" variant="subtle" />
          <Text as="p" size="sm" ml={1} variant="subtle">
            Dispute Status: {StatusDescription[status]}
          </Text>
        </Flex>
      )}
      {!isDispute && status === CareSubscriptionPaymentStatus.PastDue && (
        <Flex alignItems="center" mb={1}>
          {!paidAt ? (
            <>
              <Icon name="clock" size="sm" variant="subtle" />
              <Text as="p" size="sm" ml={1} mr={1} variant="subtle">
                {isInRetryPeriod ? 'Retrying Payment' : 'Unable to Collect'}
              </Text>
              <Tooltip
                label={
                  isInRetryPeriod
                    ? 'Attempting to collect payment from all cards on file including original payment method'
                    : "We've reached the max amount of auto retries for this payment"
                }
                placement="right"
                titleCaseLabel={false}
              >
                <Center>
                  <Icon name="questionMarkCircle" size="sm" />
                </Center>
              </Tooltip>
            </>
          ) : (
            <>
              <Icon name="checkmark" size="sm" variant="subtle" />
              <Text as="p" size="sm" ml={1} variant="subtle">
                Payment Collected: {format(paidAt, 'MM/dd/yyyy')}
              </Text>
            </>
          )}
        </Flex>
      )}
      {isDispute && isAdmin && (
        <Link
          as={ReactRouterLink}
          variant="interactive"
          to={RouteBasePaths.DisputesReporting}
          size="sm"
          iconProps={{ name: 'externalLink', size: 'sm' }}
          textDecoration="none"
        >
          View Disputes Report
        </Link>
      )}
    </Box>
  );
};
